import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress,
} from '@mui/material';
import { ContentCut, Spa, Email, CheckCircle } from '@mui/icons-material';
import authService from '../../services/auth';

const EmailConfirmation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [verifying, setVerifying] = React.useState(false);
  const [verificationResult, setVerificationResult] = React.useState(null);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState('');
  const [resendError, setResendError] = useState('');

  // Get user data from registration
  const userData = location.state?.userData || {};

  const handleResendEmail = async () => {
    setResendLoading(true);
    setResendSuccess('');
    setResendError('');
    
    try {
      await authService.resendConfirmation(userData.email);
      setResendSuccess('Confirmation email sent successfully! Please check your inbox.');
    } catch (err) {
      setResendError('Failed to resend confirmation email: ' + err.message);
    } finally {
      setResendLoading(false);
    }
  };

  const handleGoToLogin = () => {
    navigate('/login');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #dc2cb0 0%, #E91E63 50%, #8E24AA 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 3,
      }}
    >
      <Container maxWidth="md">
        <Card sx={{ boxShadow: '0 20px 40px rgba(0,0,0,0.1)' }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            {/* Logo and Brand */}
            <Box mb={4}>
              <Box display="flex" justifyContent="center" alignItems="center" mb={2}>
                <ContentCut sx={{ fontSize: 40, color: 'primary.main', mr: 1 }} />
                <Spa sx={{ fontSize: 40, color: 'secondary.main' }} />
              </Box>
              <Typography variant="h3" fontWeight="bold" gutterBottom>
                Click<span style={{ color: '#dc2cb0' }}>Looks</span>
              </Typography>
            </Box>

            {/* Success Icon */}
            <Box mb={3}>
              <CheckCircle sx={{ fontSize: 80, color: 'success.main' }} />
            </Box>

            {/* Main Message */}
            <Typography variant="h4" gutterBottom sx={{ color: 'success.main', fontWeight: 'bold' }}>
              Registration Successful! 🎉
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
              Welcome to ClickLooks, {userData.first_name || 'Salon Owner'}!
            </Typography>

            {/* Email Icon */}
            <Box mb={3}>
              <Email sx={{ fontSize: 60, color: 'primary.main' }} />
            </Box>

            {/* Instructions */}
            <Typography variant="body1" sx={{ mb: 3, fontSize: '1.1rem' }}>
              We've sent a welcome email to <strong>{userData.email}</strong> with your confirmation link.
            </Typography>

            <Typography variant="body1" sx={{ mb: 4, fontSize: '1.1rem' }}>
              Please check your inbox (and spam folder) and click the confirmation link to activate your account.
            </Typography>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleResendEmail}
                disabled={resendLoading}
                sx={{ 
                  bgcolor: 'secondary.main',
                  '&:hover': { bgcolor: 'secondary.dark' }
                }}
              >
                {resendLoading ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Sending...
                  </>
                ) : (
                  'Resend Email'
                )}
              </Button>
              
              <Button
                variant="outlined"
                size="large"
                onClick={handleGoToLogin}
                sx={{ 
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  '&:hover': { 
                    borderColor: 'primary.dark',
                    bgcolor: 'primary.main',
                    color: 'white'
                  }
                }}
              >
                Go to Login
              </Button>
            </Box>

            {/* Resend Status Messages */}
            {resendSuccess && (
              <Alert severity="success" sx={{ mt: 3, mb: 2 }}>
                {resendSuccess}
              </Alert>
            )}
            
            {resendError && (
              <Alert severity="error" sx={{ mt: 3, mb: 2 }}>
                {resendError}
              </Alert>
            )}

            {/* Additional Info */}
            <Box sx={{ mt: 4, p: 3, bgcolor: 'grey.50', borderRadius: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                <strong>What happens next?</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                • Click the confirmation link in your email
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                • Your account will be activated
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                • You can then login and start managing your salon
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • Your FREE trial starts immediately after confirmation
              </Typography>
            </Box>

            {/* Troubleshooting */}
            <Box sx={{ mt: 3, p: 2, bgcolor: 'warning.light', borderRadius: 2 }}>
              <Typography variant="body2" color="warning.dark">
                <strong>Didn't receive the email?</strong> Check your spam folder or click "Resend Email" above.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default EmailConfirmation;
